/**
 * Frontend-initiated auto-system-message bus.
 *
 * The pattern: when a frontend event happens that the agent should know
 * about (a Remotion video finished rendering, an OpenUI Lang component
 * threw a render error, etc.) we PUBLISH an event here. The chat surface
 * subscribes once, and on each published event it composes a synthetic
 * user message wrapped in `<system_message kind="..." ...attrs/>` XML
 * and calls `sendMessage()`. The agent reads it as a new turn.
 *
 * Why a bus and not direct calls: publishers (renderers nested several
 * components deep, error boundaries) and the chat surface (where
 * `sendMessage` lives) don't naturally share a context boundary. The
 * bus decouples them.
 *
 * Guarantees:
 *  - **Dedup** by `eventId`: the same event published twice is sent once.
 *    Remounts of a renderer that re-emit the same eventId are safe.
 *  - **Visibility gate**: events emitted while the document is hidden
 *    are dropped, never queued. Background tabs don't spam the agent.
 *  - **Status gate**: events emitted while the chat is streaming are
 *    queued and flushed on the next 'ready' transition.
 *  - **Per-instance dedup state**: the chat-view tracks sent eventIds in
 *    a ref. Across full page reloads, the state resets — events from a
 *    prior session won't re-fire.
 */

export interface AutoSystemMessageEvent {
  /** Unique event id used for dedup. Stable across remounts. */
  eventId: string;
  /** Discriminates handlers in the system prompt. Use kebab-case. */
  kind: 'render-complete' | 'render-error' | 'render-ready' | string;
  /**
   * Extra attributes serialized into the XML tag. Use simple string/number
   * values — they get HTML-escaped and inlined as `key="value"`.
   */
  payload?: Record<string, string | number | boolean>;
  /**
   * Optional human-readable body for the message. Most use cases don't
   * need this — the agent can infer from `kind` + attrs.
   */
  body?: string;
}

type Listener = (event: AutoSystemMessageEvent) => void;

const listeners = new Set<Listener>();

/**
 * Publish an event. The chat surface, if subscribed, will eventually
 * send it as a `<system_message ... />` user message. Dedup is enforced
 * downstream — safe to call repeatedly with the same eventId.
 */
export function emitAutoSystemMessage(event: AutoSystemMessageEvent): void {
  // Visibility gate at publish time. The chat surface also gates on its
  // side; double-gating here means publishers don't pay even the cost of
  // a no-op listener call when the tab is hidden.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return;
  }
  for (const listener of listeners) {
    try {
      listener(event);
    } catch (err) {
      console.error('[auto-system-message-bus] listener threw:', err);
    }
  }
}

/**
 * Subscribe. Returns an unsubscribe function. Typically called by
 * exactly one chat surface (chat-view or lab-chat-view) per page.
 */
export function onAutoSystemMessage(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, ' ');
}

/**
 * Format an event into the user-message text the agent will see.
 *
 *   <system_message kind="render-complete" vizId="..." videoUrl="..."/>
 *
 * Or with a body:
 *
 *   <system_message kind="render-error" vizId="...">
 *   The error message goes here.
 *   </system_message>
 */
export function formatSystemMessage(event: AutoSystemMessageEvent): string {
  const attrs = Object.entries(event.payload ?? {})
    .map(([k, v]) => `${k}="${escapeAttr(String(v))}"`)
    .join(' ');
  const open = attrs ? `<system_message kind="${event.kind}" ${attrs}` : `<system_message kind="${event.kind}"`;
  if (event.body) {
    return `${open}>\n${event.body}\n</system_message>`;
  }
  return `${open} />`;
}

/**
 * Parse the kind out of a part-text string. Used by the chat-view chip
 * renderer to label the message without re-parsing XML.
 */
export function parseSystemMessageKind(text: string): string | null {
  const match = text.match(/<system_message\s+kind="([^"]+)"/);
  return match ? match[1] : null;
}
